import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Plus, Search } from "lucide-react";
import { TablePagination } from "@/components/ui/table-pagination";
import { PermissionModal } from "@/components/permissions/PermissionModal";

interface Permission {
  id: string;
  name: string;
  module: string;
  action: string;
  slug: string;
  description: string | null;
}

export default function Permissions() {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedModule, setSelectedModule] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState<Permission | undefined>();

  const pageSize = 10;

  // Buscar permissions
  const { data: permissions = [], isLoading } = useQuery({
    queryKey: ["permissions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("permissions" as any)
        .select("*")
        .order("module", { ascending: true })
        .order("action", { ascending: true }) as any;

      if (error) throw error;
      return data as Permission[];
    },
  });

  // Filtrar permissions
  const filteredPermissions = permissions.filter((permission) => {
    const matchesSearch = permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         permission.slug.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesModule = !selectedModule || permission.module === selectedModule;
    return matchesSearch && matchesModule;
  });

  // Paginação
  const totalPages = Math.ceil(filteredPermissions.length / pageSize);
  const paginatedPermissions = filteredPermissions.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  // Módulos únicos para o filtro
  const modules = Array.from(new Set(permissions.map((p) => p.module))).sort();

  const handleNewPermission = () => {
    setSelectedPermission(undefined);
    setIsModalOpen(true);
  };

  const handleEdit = (permission: Permission) => {
    setSelectedPermission(permission);
    setIsModalOpen(true);
  };

  const getModuleBadgeColor = (module: string) => {
    const colors: Record<string, string> = {
      products: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
      clients: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      suppliers: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
      transactions: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
      users: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
      roles: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300",
      permissions: "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300",
      reports: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300",
      dashboard: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300",
      settings: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
      tenants: "bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300",
    };
    return colors[module] || "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
  };

  const getActionBadge = (action: string) => {
    const colors: Record<string, string> = {
      create: "default",
      read: "secondary",
      update: "outline",
      delete: "destructive",
      manage: "default",
      view: "secondary",
      export: "outline",
    };
    return colors[action] || "default";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-cy="permissions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-cy="permissions-title">Permissões</h1>
          <p className="text-muted-foreground">
            Gerencie as permissões do sistema
          </p>
        </div>
        <Button onClick={handleNewPermission} data-cy="new-permission-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nova Permissão
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle data-cy="permissions-list-title">Lista de Permissões</CardTitle>
          <CardDescription>
            {filteredPermissions.length} {filteredPermissions.length === 1 ? "permissão" : "permissões"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Filtros */}
            <div className="flex gap-4" data-cy="permissions-filters">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou slug..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                  data-cy="permissions-search-input"
                />
              </div>
              <select
                value={selectedModule}
                onChange={(e) => setSelectedModule(e.target.value)}
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-cy="permissions-module-filter"
              >
                <option value="">Todos os módulos</option>
                {modules.map((module) => (
                  <option key={module} value={module}>
                    {module}
                  </option>
                ))}
              </select>
            </div>

            {/* Tabela */}
            <div className="border rounded-md">
              <Table data-cy="permissions-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Módulo</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead className="w-[100px]">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-cy="permissions-table-body">
                  {paginatedPermissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground" data-cy="permissions-empty-state">
                        Nenhuma permissão encontrada
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedPermissions.map((permission) => (
                      <TableRow key={permission.id}>
                        <TableCell className="font-medium">
                          {permission.name}
                        </TableCell>
                        <TableCell>
                          <Badge className={getModuleBadgeColor(permission.module)} variant="outline">
                            {permission.module}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getActionBadge(permission.action) as any}>
                            {permission.action}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {permission.slug}
                          </code>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(permission)}
                          >
                            Editar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <TablePagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={filteredPermissions.length}
                itemsPerPage={pageSize}
                onPageChange={setPage}
                onItemsPerPageChange={() => {}}
              />
            )}
          </div>
        </CardContent>
      </Card>

      <PermissionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        permission={selectedPermission}
      />
    </div>
  );
}
